import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import { useBrands, useSubcategories } from '../hooks/useProducts';
import { formatPrice } from '../lib/utils';

interface ProductFiltersProps {
  category?: string;
  subcategory?: string;
  selectedBrands: string[];
  selectedSubcategories: string[];
  selectedProductTypes: string[];
  priceRange: [number, number];
  onBrandChange: (brandId: string) => void;
  onSubcategoryChange: (subcategoryId: string) => void;
  onProductTypeChange: (productType: string) => void;
  onPriceRangeChange: (range: [number, number]) => void;
  onClearFilters: () => void;
  showMobileFilters: boolean;
  onToggleMobileFilters: () => void;
}

const ProductFilters: React.FC<ProductFiltersProps> = ({
  category,
  subcategory,
  selectedBrands,
  selectedSubcategories,
  selectedProductTypes,
  priceRange,
  onBrandChange,
  onSubcategoryChange,
  onProductTypeChange,
  onPriceRangeChange,
  onClearFilters,
  showMobileFilters,
  onToggleMobileFilters,
}) => {
  const { brands } = useBrands();
  const { subcategories } = useSubcategories(category);
  
  const [expandedSections, setExpandedSections] = useState({
    brands: true,
    subcategories: true,
    productTypes: true,
    price: true,
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const productTypes = [
    { id: 'regular', name: 'Regular', count: 0 },
    { id: 'rental', name: 'Rental', count: 0 },
    { id: 'open-box', name: 'Open Box', count: 0 },
    { id: 'factory-returned', name: 'Factory Returned', count: 0 }
  ];

  const priceRanges = [
    { label: 'Under ₹10,000', min: 0, max: 10000 },
    { label: '₹10,000 - ₹25,000', min: 10000, max: 25000 },
    { label: '₹25,000 - ₹50,000', min: 25000, max: 50000 },
    { label: '₹50,000 - ₹1,00,000', min: 50000, max: 100000 },
    { label: 'Above ₹1,00,000', min: 100000, max: 500000 },
  ];

  const FilterSection: React.FC<{
    title: string;
    sectionKey: keyof typeof expandedSections;
    children: React.ReactNode;
  }> = ({ title, sectionKey, children }) => (
    <div className="border-b border-gray-200 pb-4 mb-4 last:border-b-0">
      <button
        onClick={() => toggleSection(sectionKey)}
        className="flex items-center justify-between w-full text-left"
      >
        <h3 className="text-md font-medium text-gray-900">{title}</h3>
        {expandedSections[sectionKey] ? (
          <ChevronUp className="h-4 w-4 text-gray-500" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-500" />
        )}
      </button>
      <AnimatePresence>
        {expandedSections[sectionKey] && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-3">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  const filtersContent = (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
        <button 
          onClick={onClearFilters}
          className="text-sm text-red-600 hover:text-red-700 font-medium"
        >
          Clear All
        </button>
      </div>

      {/* Brand Filters */}
      {brands.length > 0 && (
        <FilterSection title="Brand" sectionKey="brands">
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {brands.map(brand => (
              <label key={brand.id} className="flex items-center space-x-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded">
                <input 
                  type="checkbox"
                  checked={selectedBrands.includes(brand.id)}
                  onChange={() => onBrandChange(brand.id)}
                  className="rounded text-red-600 focus:ring-red-500 border-gray-300"
                />
                <span className="flex-1">{brand.name}</span>
                {brand.country && (
                  <span className="text-xs text-gray-500">{brand.country}</span>
                )}
              </label>
            ))}
          </div>
        </FilterSection>
      )}

      {/* Subcategory Filters */}
      {!subcategory && subcategories.length > 0 && (
        <FilterSection title="Type" sectionKey="subcategories">
          <div className="space-y-2">
            {subcategories.map(subcategoryItem => (
              <label key={subcategoryItem.id} className="flex items-center space-x-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded">
                <input 
                  type="checkbox"
                  checked={selectedSubcategories.includes(subcategoryItem.id)}
                  onChange={() => onSubcategoryChange(subcategoryItem.id)}
                  className="rounded text-red-600 focus:ring-red-500 border-gray-300"
                />
                <span>{subcategoryItem.name}</span>
              </label>
            ))}
          </div>
        </FilterSection>
      )}

      {/* Product Type Filters */}
      <FilterSection title="Product Type" sectionKey="productTypes">
        <div className="space-y-2">
          {productTypes.map(type => (
            <label key={type.id} className="flex items-center space-x-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded">
              <input 
                type="checkbox"
                checked={selectedProductTypes.includes(type.id)}
                onChange={() => onProductTypeChange(type.id)}
                className="rounded text-red-600 focus:ring-red-500 border-gray-300"
              />
              <span>{type.name}</span>
            </label>
          ))}
        </div>
      </FilterSection>

      {/* Price Range */}
      <FilterSection title="Price Range" sectionKey="price">
        <div className="space-y-3">
          {/* Quick Price Ranges */}
          <div className="space-y-2">
            {priceRanges.map((range, index) => (
              <button
                key={index}
                onClick={() => onPriceRangeChange([range.min, range.max])}
                className={`block w-full text-left text-sm p-2 rounded transition-colors ${
                  priceRange[0] === range.min && priceRange[1] === range.max
                    ? 'bg-red-100 text-red-700 border border-red-300'
                    : 'hover:bg-gray-50 border border-transparent'
                }`}
              >
                {range.label}
              </button>
            ))}
          </div>

          {/* Custom Range Slider */}
          <div className="pt-4 border-t border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Custom Range
            </label>
            <div className="px-2">
              <input 
                type="range" 
                min="0" 
                max="500000" 
                step="5000"
                value={priceRange[1]}
                onChange={(e) => onPriceRangeChange([priceRange[0], parseInt(e.target.value)])}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-600"
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-sm text-gray-600">{formatPrice(priceRange[0])}</span>
                <span className="text-sm text-gray-600">{formatPrice(priceRange[1])}</span>
              </div>
            </div>
          </div>
        </div>
      </FilterSection>
    </div>
  );

  return (
    <>
      {/* Mobile Filter Toggle */}
      <div className="lg:hidden mb-4">
        <button 
          onClick={onToggleMobileFilters} 
          className="btn btn-outline w-full flex items-center justify-center space-x-2 py-3"
        >
          <Filter className="h-4 w-4" />
          <span>{showMobileFilters ? 'Hide Filters' : 'Show Filters'}</span>
        </button>
      </div>

      {/* Desktop Filters */}
      <div className="hidden lg:block">
        <div className="sticky top-24 bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          {filtersContent}
        </div>
      </div>

      {/* Mobile Filters Overlay */}
      <AnimatePresence>
        {showMobileFilters && (
          <div className="lg:hidden fixed inset-0 z-50 flex">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={onToggleMobileFilters}
            />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              className="relative w-80 max-w-sm bg-white h-full overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
                  <button
                    onClick={onToggleMobileFilters}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                {filtersContent}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ProductFilters;